import { useState } from "react";

import { Button, ToggleButton } from "@/components/button";

import type { ChoicesData } from "../../types/mbc-chat.types";

type ChoicesPartProps = {
  choices: ChoicesData;
  isDisabled: boolean;
  onSelect: (label: string) => void;
};

export function ChoicesPart({
  choices,
  isDisabled,
  onSelect,
}: ChoicesPartProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  if (!choices.multiple) {
    return (
      <div className="mt-md flex flex-wrap gap-sm">
        {choices.options.map((option) => (
          <Button
            className="rounded-full"
            isDisabled={isDisabled}
            key={option.id}
            onPress={() => onSelect(option.label)}
            size="sm"
            variant="secondary"
          >
            {option.label}
          </Button>
        ))}
      </div>
    );
  }

  const toggle = (id: string, isSelected: boolean) => {
    setSelectedIds((previous) => {
      const next = new Set(previous);

      if (isSelected) next.add(id);
      else next.delete(id);

      return next;
    });
  };

  // Joined in the order the agent listed them, not the order they were clicked.
  const confirm = () =>
    onSelect(
      choices.options
        .filter((option) => selectedIds.has(option.id))
        .map((option) => option.label)
        .join(", "),
    );

  return (
    <div className="mt-md flex flex-col gap-md">
      <div className="flex flex-wrap gap-sm">
        {choices.options.map((option) => {
          const isSelected = selectedIds.has(option.id);

          return (
            <ToggleButton
              className="rounded-full"
              isDisabled={isDisabled}
              isSelected={isSelected}
              key={option.id}
              onChange={(value) => toggle(option.id, value)}
              size="sm"
              variant={isSelected ? "primary" : "secondary"}
            >
              {option.label}
            </ToggleButton>
          );
        })}
      </div>
      <Button
        className="self-start"
        isDisabled={isDisabled || selectedIds.size === 0}
        onPress={confirm}
        size="sm"
        variant="primary"
      >
        Confirm
      </Button>
    </div>
  );
}
